import React from 'react';
import { Title, Loader, ReloadIconButton, ClearFilters } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import {
  Box,
  Button,
  Chip,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../../hooks';
import { HealthSystemDomainUsersResultBusinessUnitUserViewModel } from '../../api/HealthSystemApi';

const UserManagementPage: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const navigate = useNavigate();

  const apiService = new ApiService();

  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [users, setUsers] = React.useState<HealthSystemDomainUsersResultBusinessUnitUserViewModel[]>([]);
  const [searchText, setSearchText] = React.useState('');

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const loadUsers = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.admin
      .adminGetUsers({ Page: searchPage, Size: rowsPerPage, SearchText: searchText })
      .then((result) => {
        setUsers(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadUsers();
    }
  }, [loaded]);

  const viewUser = (id: string) => {
    navigate(`/user-management/${id}`);
  };

  const renderUserCell = (user: HealthSystemDomainUsersResultBusinessUnitUserViewModel) => {
    return (
      <Tooltip arrow title={t('f-user-management-view')}>
        <Box>
          <Typography variant="inherit">{user.name}</Typography>
          <Typography variant="body2">{user.email}</Typography>
        </Box>
      </Tooltip>
    );
  };

  const renderActionCell = (user: HealthSystemDomainUsersResultBusinessUnitUserViewModel) => {
    return (
      <Tooltip arrow title={t('f-user-management-view')}>
        <IconButton
          id={`user-management-view-${user.id}`}
          size="small"
          color="primary"
          onClick={() => viewUser(user.id)}
          sx={V4_ICON_BUTTON_SMALL}
        >
          <Icon>{'visibility'}</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const clearFilters = () => {
    setSearchText('');
  };

  const renderFiltersSection = () => {
    return (
      <Grid container spacing={1} sx={styles.centred}>
        <Grid item>
          <TextField
            label={t('f-com-search-farm')}
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setLoaded(false);
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Backspace') {
                setLoaded(false);
              }
            }}
          />
        </Grid>

        <Grid item>
          <ClearFilters onClick={clearFilters} />
        </Grid>

        <Grid item>
          <Button
            id="searchBtn"
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setLoaded(false)}
            startIcon={<Icon>{'search'}</Icon>}
          >
            {t('f-search')}
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        {/* title & spinner */}

        <Grid container direction="row" spacing={1} sx={styles.paperHeader}>
          <Grid item container md={6} xs={10} spacing={1}>
            <Grid item>
              <Icon fontSize="large" color="primary">
                {'local_pharmacy'}
              </Icon>
            </Grid>
            <Grid item>
              <Title>{t('f-user-management')}</Title>
            </Grid>
            <Grid item>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>

          <Grid item container sx={styles.justifyRight} md={6} xs={10}>
            <Grid item>
              <ReloadIconButton action={() => setLoaded(false)} />
            </Grid>
          </Grid>
        </Grid>

        {renderFiltersSection()}

        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

        {/* data table */}
        {loaded && (
          <Grid item md={12} xs={12}>
            <Paper elevation={1}>
              <Table size="small" id="users-result-table" sx={V4_TABLE}>
                <TableHead sx={styles.tableHeader}>
                  <TableRow>
                    <TableCell>{t('f-com-name')}</TableCell>
                    <TableCell>{t('f-com-total-business-units')}</TableCell>
                    <TableCell>{''}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell variant="head">{renderUserCell(user)}</TableCell>
                      <TableCell>{user.totalBusinessUnits}</TableCell>
                      <TableCell>{renderActionCell(user)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                      count={total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default UserManagementPage;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    tableHeader: {
      background: colors.defaultGrey
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    },
    tableHeaderFooter: {
      background: colors.defaultGrey
    },
    centred: {
      alignItems: 'center'
    },
    vertName: {},
    vertNameDeleted: {
      color: colors.red
    }
  }) as const;
